import { ClipboardList, FileText, Plus, Receipt, Zap } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type NavigateFunction, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/components/ui/utils";
import { trackAction } from "@/integrations/datadog/track";
import {
  type DocumentCreateKind,
  type DocumentCreateLauncher,
  useDocumentCreateLauncher,
} from "@/lib/document-create-launcher";

const FAB_BOTTOM = "bottom-(--launcher-bottom)";

/*
 * From `md` the sidebar is a fixed column, so the desktop button shifts past
 * it into the content area and follows the sidebar's collapse transition. In
 * the installed app the sidebar starts at the left safe-area inset
 * (`--shell-inset-left`, 0 in a browser tab), so the offset adds the inset to
 * keep the button clear of the sidebar's right edge.
 */
const DESKTOP_FAB_OFFSET =
  "transition-transform duration-200 ease-linear md:translate-x-[calc(var(--sidebar-width)+var(--shell-inset-left))] md:group-has-data-[collapsible=icon]/sidebar-wrapper:translate-x-[calc(var(--sidebar-width-icon)+var(--shell-inset-left))]";

function getFabVisibility(pathname: string) {
  const normalizedPathname = pathname.replace(/\/+$/, "");
  const isDocumentCreationRoute = [
    "/invoices/create",
    "/estimates/create",
    "/purchase-orders/create",
  ].includes(normalizedPathname);
  return isDocumentCreationRoute
    ? { desktop: "hidden xl:flex", mobile: "hidden" }
    : { desktop: "hidden md:flex", mobile: "md:hidden" };
}

interface QuickAction {
  color: string;
  destination: string;
  /** When set and a launcher is mounted, opens the guided-steps dialog in place. */
  dialogKind?: DocumentCreateKind;
  icon: typeof Receipt;
  id: string;
  label: string;
}

const QUICK_ACTIONS: readonly QuickAction[] = [
  {
    id: "billing",
    label: "New PO",
    icon: Receipt,
    destination: "/purchase-orders/new",
    dialogKind: "purchase_order",
    color: "bg-success text-success-on-solid hover:bg-success/90",
  },
  {
    id: "invoice",
    label: "Create Invoice",
    icon: FileText,
    destination: "/invoices/new",
    dialogKind: "invoice",
    color: "bg-info text-info-on-solid hover:bg-info/90",
  },
  {
    id: "estimate",
    label: "New Estimate",
    icon: ClipboardList,
    destination: "/estimates/new",
    dialogKind: "estimate",
    color: "bg-processing text-processing-on-solid hover:bg-processing/90",
  },
];

/** Opens the dialog when the action and shell support it, else navigates. */
function launchQuickAction(
  action: QuickAction,
  launcher: DocumentCreateLauncher | null,
  navigate: NavigateFunction,
) {
  const dialogKind = launcher ? action.dialogKind : undefined;
  trackAction("cta.fab.quick_action.clicked", {
    action_id: action.id,
    destination: action.destination,
  });
  if (dialogKind) {
    launcher?.open(dialogKind, "floating-action-button");
    return;
  }
  void navigate(action.destination);
}

export function FloatingActionButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const visibility = getFabVisibility(useLocation().pathname);
  const navigate = useNavigate();
  const launcher = useDocumentCreateLauncher();
  const desktopFabRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (desktopFabRef.current && !desktopFabRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen, handleClickOutside]);

  const quickActions = useMemo(
    () =>
      QUICK_ACTIONS.map((action) => ({
        ...action,
        action: () => {
          setIsSheetOpen(false);
          setIsOpen(false);
          launchQuickAction(action, launcher, navigate);
        },
      })),
    [launcher, navigate],
  );

  return (
    <>
      {/* Desktop FAB */}
      <div
        className={cn(
          "fixed left-6 z-50 flex-col gap-2",
          visibility.desktop,
          FAB_BOTTOM,
          DESKTOP_FAB_OFFSET,
        )}
        ref={desktopFabRef}
      >
        {isOpen && (
          <div className="slide-in-from-bottom-2 flex animate-in flex-col gap-2 motion-reduce:animate-none">
            {quickActions.map((action) => (
              <Button
                className={cn(
                  "size-12 rounded-full shadow-lg transition-[transform,colors] duration-200 hover:scale-105",
                  action.color,
                )}
                key={action.id}
                onClick={action.action}
                size="lg"
              >
                <action.icon className="size-5" />
                <span className="sr-only">{action.label}</span>
              </Button>
            ))}
          </div>
        )}
        <Button
          aria-expanded={isOpen}
          className={cn(
            "size-14 rounded-full bg-primary shadow-lg transition-[transform,colors] duration-200 hover:bg-primary/90",
            isOpen ? "rotate-45" : "hover:scale-105",
          )}
          onClick={() => setIsOpen(!isOpen)}
          size="lg"
        >
          <Plus className="size-6" />
          <span className="sr-only">{isOpen ? "Close Quick Actions" : "Quick Actions"}</span>
        </Button>
      </div>

      {/* Mobile Sheet */}
      <div
        className={cn(
          "fixed left-[calc(1.5rem+var(--shell-inset-left))] z-50",
          visibility.mobile,
          FAB_BOTTOM,
        )}
      >
        <Sheet onOpenChange={setIsSheetOpen} open={isSheetOpen}>
          <SheetTrigger asChild>
            <Button
              className="size-14 rounded-full bg-primary shadow-lg transition-[transform,colors] duration-200 hover:scale-105 hover:bg-primary/90"
              size="lg"
            >
              <Zap className="size-6" />
              <span className="sr-only">Quick Actions</span>
            </Button>
          </SheetTrigger>
          <SheetContent className="h-auto" side="bottom">
            <SheetHeader className="text-left">
              <SheetTitle>Quick Actions</SheetTitle>
            </SheetHeader>
            <div className="mt-6 mb-4 grid grid-cols-2 gap-4">
              {quickActions.map((action) => (
                <Button
                  className={cn("h-16 flex-col gap-2 transition-colors duration-200", action.color)}
                  key={action.id}
                  onClick={action.action}
                  size="lg"
                >
                  <action.icon className="size-6" />
                  <span className="text-sm">{action.label}</span>
                </Button>
              ))}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
}
